import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

import * as Dialog from "@radix-ui/react-dialog";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { ConfigProvider } from "antd";
import { cx } from "class-variance-authority";

import closeSvg from "../../img/close.svg";
import { rawSpacing } from "../../styles/theme.css";
import { hasHardwareCompositing } from "../../utils/hardwareCompositing";
import { PendoProps } from "../../utils/pendo";
import { Svg } from "../../utils/svgIcon";
import { Tooltip } from "../Tooltip";

import * as styles from "./Modal.css";

export type ModalVariant = "dialog" | "content";

/** Pixels as a number, or a percentage of the viewport. */
export type ModalWidth = number | `${number}%`;

// Per-variant defaults: a content modal is wider, and scrolls its body so the
// dividers stay put. Both are still overridable.
const DEFAULT_WIDTH: Record<ModalVariant, number> = {
    dialog: 850,
    content: 720,
};

// The gutter the overlay keeps on each side, so `styles.overlay` and the check
// below agree on when the card stops fitting.
const GUTTER = rawSpacing[4];

// Percentage widths always fit, so only a pixel width can outgrow the viewport.
function outgrowsViewport(width: ModalWidth) {
    return typeof width === "number" && window.innerWidth < width + GUTTER * 2;
}

export interface ModalProps extends PendoProps {
    /**
     * `dialog` asks the user something — a message and an action row.
     * `content` holds things to interact with, and divides off its header and
     * footer to frame them.
     */
    variant: ModalVariant;
    open: boolean;
    /** Called when the user dismisses the modal: close button, Esc or backdrop. */
    onClose: () => void;
    title?: ReactNode;
    /** Rendered before the title, e.g. a status icon. */
    icon?: ReactNode;
    /** Rendered right after the title, e.g. a status tag. */
    titleExtra?: ReactNode;
    children?: ReactNode;
    /** Buttons for the bottom right action row. */
    footer?: ReactNode;
    /**
     * Numbers are pixels; percentages are relative to the viewport (e.g.
     * `"80%"`). Defaults to the variant's width.
     */
    width?: ModalWidth;
    hideCloseButton?: boolean;
    /** Keep the modal open on Esc and backdrop clicks. */
    disableDismiss?: boolean;
    /**
     * Cap the modal at the viewport and scroll the body inside it, keeping the
     * header and footer in place. Otherwise the whole modal scrolls. Defaults to
     * on for `content`, off for `dialog`.
     */
    scrollBody?: boolean;
    /**
     * Keep the modal centred with its backdrop showing even when it is wider
     * than the viewport. Otherwise it fills the screen at a tighter padding.
     */
    disableFullscreen?: boolean;
    /** Accessible name for modals without a visible title. */
    ariaLabel?: string;
    /** Portal target. Defaults to `document.body`. */
    container?: HTMLElement | null;
    className?: string;
    bodyClassName?: string;
}

export function Modal({
    variant,
    open,
    onClose,
    title,
    icon,
    titleExtra,
    children,
    footer,
    width,
    hideCloseButton = false,
    disableDismiss = false,
    scrollBody,
    disableFullscreen = false,
    ariaLabel,
    container,
    className,
    bodyClassName,
    "data-pendo": dataPendo,
}: ModalProps) {
    const resolvedWidth = width ?? DEFAULT_WIDTH[variant];
    const surfaceRef = useRef<HTMLDivElement>(null);
    // antd portals its popups (selects, pickers, tooltips) to the page, where
    // the dialog leaves them dead to clicks. Keep them inside. Drop with antd.
    // Resolved lazily and never re-created: a new identity here would push a new
    // antd config down and re-render every antd component in the dialog.
    const getPopupContainer = useCallback(
        () => surfaceRef.current ?? document.body,
        []
    );
    const [titleEl, setTitleEl] = useState<HTMLHeadingElement | null>(null);
    const [titleTruncated, setTitleTruncated] = useState(false);
    // Seeded rather than measured in an effect, so the card doesn't open centred
    // and then snap to fullscreen a frame later.
    const [cramped, setCramped] = useState(() =>
        outgrowsViewport(resolvedWidth)
    );

    // Titles are held to one line, so the full text is only reachable through a
    // tooltip — and only worth offering when it actually got cut off. Measured as
    // the heading lands, which is before the browser has painted it.
    const measureTitle = useCallback((heading: HTMLHeadingElement | null) => {
        setTitleEl(heading);
        setTitleTruncated(heading !== null && isTruncated(heading));
    }, []);

    // The dialog is capped at the viewport width, so resizing can cut a title
    // that used to fit, or reveal one that didn't.
    useEffect(() => {
        if (!titleEl) {
            return;
        }
        const remeasure = () => setTitleTruncated(isTruncated(titleEl));
        window.addEventListener("resize", remeasure);
        return () => window.removeEventListener("resize", remeasure);
    }, [titleEl]);

    // Resizing can take the viewport under the card's width, or back over it.
    useEffect(() => {
        const recheck = () => setCramped(outgrowsViewport(resolvedWidth));
        recheck();
        window.addEventListener("resize", recheck);
        return () => window.removeEventListener("resize", recheck);
    }, [resolvedWidth]);

    const hasHeader = Boolean(title) || !hideCloseButton;
    const fullscreen = cramped && !disableFullscreen;
    const cssWidth =
        typeof resolvedWidth === "number"
            ? `${resolvedWidth}px`
            : resolvedWidth;
    const scrolls = scrollBody ?? variant === "content";

    function onDismiss(event: Event) {
        if (disableDismiss) {
            event.preventDefault();
        }
    }

    return (
        <Dialog.Root
            open={open}
            onOpenChange={(isOpen) => !isOpen && onClose()}
        >
            <Dialog.Portal container={container ?? undefined}>
                {/* The card sits inside the overlay so the overlay can scroll it. */}
                <Dialog.Overlay
                    className={cx(
                        styles.overlay,
                        fullscreen && styles.overlayFullscreen
                    )}
                    data-blur={hasHardwareCompositing()}
                >
                    <Dialog.Content
                        className={cx(
                            styles.content,
                            scrolls && styles.contentScrollBody,
                            fullscreen && styles.contentFullscreen,
                            className
                        )}
                        style={assignInlineVars({
                            [styles.widthVar]: cssWidth,
                        })}
                        onInteractOutside={onDismiss}
                        onEscapeKeyDown={onDismiss}
                        aria-describedby={undefined}
                        data-pendo={dataPendo}
                    >
                        <div
                            ref={surfaceRef}
                            className={styles.surfaceType[variant]}
                        >
                            <ConfigProvider
                                getPopupContainer={getPopupContainer}
                            >
                                {!title && (
                                    <Dialog.Title className={styles.srOnly}>
                                        {ariaLabel ?? "Dialog"}
                                    </Dialog.Title>
                                )}
                                {hasHeader && (
                                    <div className={styles.headerType[variant]}>
                                        {icon && (
                                            <span className={styles.headerIcon}>
                                                {icon}
                                            </span>
                                        )}
                                        {title && (
                                            <div className={styles.titleRow}>
                                                <Tooltip
                                                    title={title}
                                                    noWrapper
                                                    disabled={!titleTruncated}
                                                    overlayClassName={
                                                        styles.titleTooltip
                                                    }
                                                >
                                                    <Dialog.Title
                                                        ref={measureTitle}
                                                        className={cx(
                                                            styles.titleType[
                                                                variant
                                                            ],
                                                            titleExtra &&
                                                                styles.titleWithExtra
                                                        )}
                                                    >
                                                        {title}
                                                    </Dialog.Title>
                                                </Tooltip>
                                                {titleExtra && (
                                                    <span
                                                        className={
                                                            styles.titleExtra
                                                        }
                                                    >
                                                        {titleExtra}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {!hideCloseButton && (
                                            <Dialog.Close
                                                type="button"
                                                className={styles.closeButton}
                                                aria-label={
                                                    ariaLabel ?? "Close"
                                                }
                                            >
                                                <Svg src={closeSvg} />
                                            </Dialog.Close>
                                        )}
                                    </div>
                                )}
                                <div
                                    className={cx(
                                        styles.bodyType[variant],
                                        scrolls && styles.bodyScroll,
                                        bodyClassName
                                    )}
                                >
                                    {children}
                                </div>
                                {footer && (
                                    <div className={styles.footerType[variant]}>
                                        {footer}
                                    </div>
                                )}
                            </ConfigProvider>
                        </div>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

function isTruncated(element: HTMLElement) {
    // Layout widths, so the open animation's transform doesn't skew them.
    return element.scrollWidth > element.clientWidth;
}
